import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// Fix mobile zoom issue: Reset zoom when input loses focus
if (typeof window !== 'undefined') {
  // Check if we're on mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;
  
  if (isMobile) {
    // Add event listener to reset zoom when inputs lose focus
    const setupZoomReset = () => {
      try {
        const resetZoom = () => {
          try {
            // Reset viewport zoom by temporarily changing the viewport meta tag
            const viewport = document.querySelector('meta[name="viewport"]');
            if (viewport) {
              // Temporarily disable user scaling to force zoom reset
              viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover');
              // Use requestAnimationFrame for smoother reset
              requestAnimationFrame(() => {
                setTimeout(() => {
                  if (viewport) {
                    viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes, viewport-fit=cover');
                  }
                }, 100);
              });
            }
          } catch (error) {
            // Silently handle errors
            if (process.env.NODE_ENV === 'development') {
              console.warn('Error resetting zoom:', error);
            }
          }
        };

        // Listen for blur events on all input and textarea elements
        const handleBlur = (e) => {
          if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
            resetZoom();
          }
        };

        // Use capture phase to catch blur events
        document.addEventListener('blur', handleBlur, true);
      } catch (error) {
        // Silently handle setup errors
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error setting up zoom reset:', error);
        }
      }
    };

    // Setup when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupZoomReset);
    } else {
      setupZoomReset();
    }
  }
}

// Global error handlers to prevent popups
if (typeof window !== 'undefined') {
  // Protect window.ethereum from undefined errors (crypto wallet extensions)
  try {
    if (!window.ethereum) {
      Object.defineProperty(window, 'ethereum', {
        value: null,
        writable: true,
        configurable: true
      });
    }
    
    // Protect ethereum.selectedAddress access
    if (window.ethereum && typeof window.ethereum === 'object') {
      if (!window.ethereum.hasOwnProperty('selectedAddress')) {
        Object.defineProperty(window.ethereum, 'selectedAddress', {
          value: null,
          writable: true,
          configurable: true
        });
      }
    }
  } catch (e) {
    // Silently handle ethereum property errors
  }

  // Prevent unhandled errors from showing popups (including Script errors from CORS)
  window.addEventListener('error', (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    // Silently handle errors - don't show popups
    return false;
  }, true);

  // Also catch errors at bubble phase
  window.addEventListener('error', (event) => {
    event.preventDefault();
    event.stopPropagation();
    return false;
  });

  // Prevent unhandled promise rejections from showing popups
  window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Silently handle promise rejections - don't show popups
    return false;
  });

  // Override console.error to prevent alerts on mobile
  const originalConsoleError = console.error;
  console.error = (...args) => {
    // Silently suppress all console.error calls to prevent popups
    // Only log in development if needed
    if (process.env.NODE_ENV === 'development') {
      // Only log non-script errors in development
      const errorString = args.join(' ');
      if (!errorString.includes('Script error') && !errorString.includes('ethereum')) {
        originalConsoleError.apply(console, args);
      }
    }
  };
}

// React Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Silently log error - don't show popups
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI instead of crashing
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          fontFamily: 'DM Sans, sans-serif',
          color: '#2d3649'
        }}>
          <h1>Something went wrong</h1>
          <p>Please refresh the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

// Safely get root element with error handling
try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
} catch (error) {
  // Silently handle errors - don't show popups
  if (process.env.NODE_ENV === 'development') {
    console.warn('Failed to render app:', error);
  }
  // Fallback: try to create root element if it doesn't exist
  try {
    if (!document.getElementById('root')) {
      const rootDiv = document.createElement('div');
      rootDiv.id = 'root';
      if (document.body) {
        document.body.appendChild(rootDiv);
        const root = ReactDOM.createRoot(rootDiv);
        root.render(
          <React.StrictMode>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </React.StrictMode>
        );
      }
    }
  } catch (fallbackError) {
    // Silently handle fallback errors
    if (process.env.NODE_ENV === 'development') {
      console.warn('Fallback render failed:', fallbackError);
    }
  }
}
